import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "@/utils/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "react-i18next";
import UpgradeModal from "@/components/UpgradeModal";
import ConfirmDialog from "@/components/ConfirmDialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Edit, Trash2, Users, Mail, Phone, MapPin } from "lucide-react";
// Feb 2026 — DACH-Smart-Inputs (Phase 4)
import { PhoneInput, PostcodeInput, VATInput } from "@/components/inputs/SmartInputs";

export default function Customers() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [customers, setCustomers] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [upgradeMessage, setUpgradeMessage] = useState("");
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, customerId: null });
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    street: "",
    street_number: "",
    postal_code: "",
    city: "",
    country_code: "",
    vat_id: "",
  });

  useEffect(() => {
    fetchCustomers();
  }, []);

  const emptyForm = () => ({
    name: "", email: "", phone: "", address: "",
    street: "", street_number: "", postal_code: "", city: "",
    country_code: user?.country || "", vat_id: "",
  });

  const openNewCustomerDialog = () => {
    setEditingCustomer(null);
    setFormData(emptyForm());
    setDialogOpen(true);
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("create") === "1") {
      openNewCustomerDialog();
      params.delete("create");
      const nextSearch = params.toString();
      navigate({ search: nextSearch ? `?${nextSearch}` : "" }, { replace: true });
    }
  }, [location.search, navigate]);

  const fetchCustomers = async () => {
    try {
      const response = await api.get('/customers');
      setCustomers(response.data);
    } catch (error) {
      console.error("Error fetching customers:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCustomer) {
        await api.put(`/customers/${editingCustomer.id}`, formData);
        toast.success("Kunde aktualisiert");
      } else {
        await api.post('/customers', formData);
        toast.success("Kunde erstellt");
      }

      setDialogOpen(false);
      setEditingCustomer(null);
      setFormData(emptyForm());
      fetchCustomers();
    } catch (error) {
      if (error.response?.status === 403) {
        // Close dialog immediately so modal is visible
        setDialogOpen(false);
        setEditingCustomer(null);
        setFormData(emptyForm());
        
        // Show upgrade modal
        setUpgradeMessage(error.response.data.detail);
        setUpgradeModalOpen(true);
      } else {
        toast.error(t('customers.saveError', 'Error saving client'));
      }
    }
  };

  const deleteCustomer = async (id) => {
    try {
      await api.delete(`/customers/${id}`);
      toast.success(t('customers.deleted', 'Client deleted'));
      fetchCustomers();
      setConfirmDialog({ isOpen: false, customerId: null });
    } catch (error) {
      toast.error(t('common.deleteError', 'Error deleting'));
    }
  };

  const handleDeleteClick = (id) => {
    setConfirmDialog({ isOpen: true, customerId: id });
  };

  return (
    <div className="space-y-8" data-testid="customers-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 mb-2" style={{ fontFamily: "Manrope, sans-serif" }}>{t('customers.title')}</h1>
          <p className="text-lg text-gray-600">{t('customers.subtitle')}</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-customer-button" onClick={openNewCustomerDialog}>
              <Plus className="h-4 w-4 mr-2" />
              {t('customers.newCustomer')}
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingCustomer ? t('customers.editCustomer') : t('customers.newCustomer')}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>{t('customers.name')} *</Label>
                <Input
                  data-testid="customer-name-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="z.B. Max Mustermann GmbH"
                  required
                />
              </div>
              <div>
                <Label>{t('customers.email')}</Label>
                <Input
                  data-testid="customer-email-input"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="kontakt@beispiel.de"
                />
              </div>
              <div>
                <Label>{t('customers.phone')}</Label>
                <PhoneInput
                  country={formData.country_code || user?.country || 'DE'}
                  value={formData.phone}
                  onChange={(v) => setFormData({ ...formData, phone: v })}
                  label={null}
                  data-testid="customer-phone-input"
                />
              </div>
              <div className="space-y-3">
                <p className="text-sm font-semibold text-gray-700">{t('customers.address', 'Address')}</p>
                
                <div className="grid grid-cols-3 gap-3">
                  <div className="col-span-2">
                    <Label htmlFor="street">{t('customers.street', 'Street')}</Label>
                    <Input
                      id="street"
                      placeholder={t('customers.streetPlaceholder', 'Street name')}
                      value={formData.street}
                      onChange={(e) => setFormData({ ...formData, street: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="street_number">{t('customers.streetNumber', 'No.')}</Label>
                    <Input
                      id="street_number"
                      placeholder="123"
                      value={formData.street_number}
                      onChange={(e) => setFormData({ ...formData, street_number: e.target.value })}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label htmlFor="postal_code">{t('customers.postalCode', 'ZIP')}</Label>
                    <PostcodeInput
                      country={formData.country_code || user?.country || 'DE'}
                      value={formData.postal_code}
                      onChange={(v) => setFormData({ ...formData, postal_code: v })}
                      label={null}
                      data-testid="customer-postal-code"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label htmlFor="city">{t('customers.city', 'City')}</Label>
                    <Input
                      id="city"
                      placeholder={t('customers.cityPlaceholder', 'City name')}
                      value={formData.city}
                      onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="country_code">{t('customers.countryCode', 'Country')} *</Label>
                  <select
                    id="country_code"
                    value={formData.country_code}
                    onChange={(e) => setFormData({ ...formData, country_code: e.target.value })}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    required
                    data-testid="customer-country-select"
                  >
                    <option value="">{t('common.pleaseSelect', 'Please select...')}</option>
                    <option value="DE">🇩🇪 Germany</option>
                    <option value="AT">🇦🇹 Austria</option>
                    <option value="CH">🇨🇭 Switzerland</option>
                    <option value="FR">🇫🇷 France</option>
                    <option value="IT">🇮🇹 Italy</option>
                    <option value="NL">🇳🇱 Netherlands</option>
                    <option value="BE">🇧🇪 Belgium</option>
                    <option value="LU">🇱🇺 Luxembourg</option>
                    <option value="LI">🇱🇮 Liechtenstein</option>
                  </select>
                </div>

                <p className="text-xs text-gray-500">
                  ⚠️ {t('customers.structuredAddressNote', 'Structured address required for Swiss QR invoices (mandatory from 21.11.2025)')}
                </p>
              </div>
              <div>
                <Label>USt-IdNr. / UID / CHE-Nummer (optional)</Label>
                <VATInput
                  country={formData.country_code || user?.country || 'DE'}
                  value={formData.vat_id}
                  onChange={(v) => setFormData({ ...formData, vat_id: v })}
                  label={null}
                  data-testid="customer-vat-input"
                />
                <p className="text-xs text-gray-500 mt-1">Für B2B-Rechnungen wichtig (besonders AT/CH)</p>
              </div>
              <Button type="submit" className="w-full" data-testid="save-customer-button">
                {editingCustomer ? t('timeTracking.update') : t('timeTracking.create')}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {customers.map((customer) => (
          <Card key={customer.id} className="card-hover" data-testid={`customer-card-${customer.id}`}>
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-3">
                  <div className="p-2 bg-purple-50 rounded-lg">
                    <Users className="h-5 w-5 text-purple-600" />
                  </div>
                  <CardTitle className="text-lg">{customer.name}</CardTitle>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 mb-4">
                {customer.email && (
                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <Mail className="h-4 w-4 flex-shrink-0" />
                    <span className="truncate">{customer.email}</span>
                  </div>
                )}
                {customer.phone && (
                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <Phone className="h-4 w-4 flex-shrink-0" />
                    <span>{customer.phone}</span>
                  </div>
                )}
                {customer.address && (
                  <div className="flex items-start space-x-2 text-sm text-gray-600">
                    <MapPin className="h-4 w-4 flex-shrink-0 mt-0.5" />
                    <span>{customer.address}</span>
                  </div>
                )}
              </div>
              <div className="flex space-x-2">
                <Button
                  data-testid={`edit-customer-${customer.id}`}
                  size="sm"
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    setEditingCustomer(customer);
                    setFormData({
                      name: customer.name,
                      email: customer.email || "",
                      phone: customer.phone || "",
                      address: customer.address || "",
                      street: customer.street || "",
                      street_number: customer.street_number || "",
                      postal_code: customer.postal_code || "",
                      city: customer.city || "",
                      country_code: customer.country_code || "",
                      vat_id: customer.vat_id || "",
                    });
                    setDialogOpen(true);
                  }}
                >
                  <Edit className="h-4 w-4 mr-1" />
                  {t('common.edit')}
                </Button>
                <Button
                  data-testid={`delete-customer-${customer.id}`}
                  size="sm"
                  variant="outline"
                  onClick={() => handleDeleteClick(customer.id)}
                >
                  <Trash2 className="h-4 w-4 text-red-600" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {customers.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center text-gray-500">
            {t('customers.noCustomers')}
          </CardContent>
        </Card>
      )}
      
      <UpgradeModal
        isOpen={upgradeModalOpen}
        onClose={() => setUpgradeModalOpen(false)}
        message={upgradeMessage}
        currentPlan={user?.plan}
      />

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ isOpen: false, customerId: null })}
        onConfirm={() => deleteCustomer(confirmDialog.customerId)}
        title={t('customers.confirmDelete', 'Kunde löschen?')}
        description={t('customers.confirmDeleteDescription', 'Diese Aktion kann nicht rückgängig gemacht werden.')}
        confirmText={t('common.delete', 'Löschen')}
        cancelText={t('common.cancel', 'Abbrechen')}
        variant="destructive"
      />
    </div>
  );
}